import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Paper,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
  Container,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Google as GoogleIcon,
  GitHub as GitHubIcon,
} from "@mui/icons-material";
import { useAlert } from "../context/AlertContext";
import { LoginRequest } from "../types";
import { useAuth0 } from "@auth0/auth0-react";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const alert = useAlert();

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    navigate("/");
    return null;
  }

  // Form state
  const [formData, setFormData] = useState<LoginRequest>({
    email: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null); // Clear error when user types
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  // Handle form submission
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      await loginWithRedirect({
        appState: {
          returnTo: "/",
        },
      });
    } catch (error) {
      console.error("Login error:", error);
      setError("Authentication failed. Please try again.");
      alert.error("Login failed. Please try again.");
      setLoading(false);
    }
  };

  // Auth0 login handlers
  const handleAuth0Login = () => {
    loginWithRedirect({
      appState: {
        returnTo: "/",
      },
    });
  };

  // Handle social logins
  const handleGoogleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: "google-oauth2",
      },
      appState: {
        returnTo: "/",
      },
    });
  };

  const handleGitHubLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: "github",
      },
      appState: {
        returnTo: "/",
      },
    });
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: { xs: 4, sm: 8 },
          mb: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            borderRadius: 2,
            maxWidth: 500,
          }}
        >
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography
              component="h1"
              variant="h4"
              fontWeight="bold"
              gutterBottom
            >
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access your Promptfoo Manager account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                my: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    value="remember"
                    color="primary"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label="Remember me"
              />
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
              >
                Forgot password?
              </Link>
            </Box>

            <LoadingButton
              fullWidth
              variant="contained"
              color="primary"
              loading={loading}
              type="submit"
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={(e) => {
                e.preventDefault();
                handleAuth0Login();
              }}
            >
              Sign In
            </LoadingButton>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                sx={{
                  py: 1,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
                onClick={handleGoogleLogin}
              >
                Google
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GitHubIcon />}
                sx={{
                  py: 1,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
                onClick={handleGitHubLogin}
              >
                GitHub
              </Button>
            </Stack>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2">
                Don't have an account?{" "}
                <Link
                  component={RouterLink}
                  to="/register"
                  variant="body2"
                  fontWeight="bold"
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
